/**
 * UI gates for ride stations & passengers (mirrors BE validation).
 *
 * Data split (same as BE):
 * - passenger.siteId / unitId → used here for delete / edit / single-pax UI
 * - ride.siteId → NOT used here for ACL (BE rideSiteAccessQuery checks ride.siteId separately)
 *
 * Action cases:
 * 1. Delete / remove station — EVERY unit + EVERY site; create_other_site ⇒ unit only
 * 2. Drag / reorder station — no unit / site ACL (legacy)
 * 3. Drag / move passenger to another ride or plus — unit + site; create_other_site ⇒ unit only
 * 4. Sliding-panel passenger drag within the same ride — allowed even for foreign unit/site
 * 5. Remove passenger — unit + site; create_other_site ⇒ unit only
 * 6. Edit passenger row — unit + site (missing ⇒ unrestricted)
 *
 * Other:
 * - missing / falsy unitId or siteId ⇒ unrestricted for that dimension (no-site)
 */

import { errors } from "../../constants";

export type TPassengerWithScope = {
  unitId?: number | null;
  siteId?: number | null;
};

export type TProfileAuthorizationCheck = (props: {
  siteIds?: number[];
  unitIds?: number[];
}) => boolean;

/**
 * Unit gate for one passenger.
 * Cases: has unit → must be in profile scope; no unit → allow.
 */
export const isPassengerAuthorizedByUnitScope = (
  passenger: TPassengerWithScope,
  profileAuthorizationInCheck: TProfileAuthorizationCheck,
): boolean =>
  profileAuthorizationInCheck({
    unitIds:
      passenger.unitId && Number(passenger.unitId)
        ? [Number(passenger.unitId)]
        : undefined,
  });

/**
 * Site gate for one passenger.
 * Cases: has site → must be in profile scope; no site → allow (no-site).
 */
export const isPassengerAuthorizedBySiteScope = (
  passenger: TPassengerWithScope,
  profileAuthorizationInCheck: TProfileAuthorizationCheck,
): boolean =>
  profileAuthorizationInCheck({
    siteIds:
      passenger.siteId && Number(passenger.siteId)
        ? [Number(passenger.siteId)]
        : undefined,
  });

export type TSiteScopeRemoveOptions = {
  /** ride.create_other_site — remove/move bypass for site gate (unit still required) */
  hasCreateOtherSite?: boolean;
};

/** Remove passenger UI gate. Default: unit + site. create_other_site: unit only. */
export const canRemovePassengerByProfileScope = (
  passenger: TPassengerWithScope,
  profileAuthorizationInCheck: TProfileAuthorizationCheck,
  { hasCreateOtherSite = false }: TSiteScopeRemoveOptions = {},
): boolean => {
  if (hasCreateOtherSite) {
    return isPassengerAuthorizedByUnitScope(
      passenger,
      profileAuthorizationInCheck,
    );
  }

  return isPassengerAuthorizedByUnitAndSiteScope(
    passenger,
    profileAuthorizationInCheck,
  );
};

export type TPassengerScopeRestrictions = {
  isForeignUnit: boolean;
  isForeignSite: boolean;
};

export const getPassengerScopeRestrictions = (
  passenger: TPassengerWithScope,
  profileAuthorizationInCheck: TProfileAuthorizationCheck,
): TPassengerScopeRestrictions => ({
  isForeignUnit: !isPassengerAuthorizedByUnitScope(
    passenger,
    profileAuthorizationInCheck,
  ),
  isForeignSite: !isPassengerAuthorizedBySiteScope(
    passenger,
    profileAuthorizationInCheck,
  ),
});

export const hasPassengerScopeRestrictions = (
  restrictions: TPassengerScopeRestrictions,
): boolean => restrictions.isForeignUnit || restrictions.isForeignSite;

export const mergePassengerScopeRestrictions = (
  restrictions: TPassengerScopeRestrictions[],
): TPassengerScopeRestrictions => ({
  isForeignUnit: restrictions.some(item => item.isForeignUnit),
  isForeignSite: restrictions.some(item => item.isForeignSite),
});

export const getPassengerMoveScopeRestrictions = ({
  restrictions,
  hasCreateOtherSite = false,
}: {
  restrictions: TPassengerScopeRestrictions;
  hasCreateOtherSite?: boolean;
}): TPassengerScopeRestrictions => ({
  isForeignUnit: restrictions.isForeignUnit,
  isForeignSite: hasCreateOtherSite ? false : restrictions.isForeignSite,
});

export const hasMoveScopeRestrictions = ({
  restrictions,
  hasCreateOtherSite = false,
}: {
  restrictions: TPassengerScopeRestrictions;
  hasCreateOtherSite?: boolean;
}): boolean =>
  hasPassengerScopeRestrictions(
    getPassengerMoveScopeRestrictions({ restrictions, hasCreateOtherSite }),
  );

export const getPassengerMoveScopeTranslationKey = ({
  restrictions,
  hasCreateOtherSite = false,
}: {
  restrictions: TPassengerScopeRestrictions;
  hasCreateOtherSite?: boolean;
}): string | undefined => {
  const moveRestrictions = getPassengerMoveScopeRestrictions({
    restrictions,
    hasCreateOtherSite,
  });

  if (moveRestrictions.isForeignUnit && moveRestrictions.isForeignSite) {
    return errors.ride.cannotMovePassengersFromDifferentUnitsAndSites;
  }

  if (moveRestrictions.isForeignUnit) {
    return errors.ride.cannotMovePassengersFromDifferentUnits;
  }

  if (moveRestrictions.isForeignSite) {
    return errors.ride.cannotMovePassengersFromDifferentSites;
  }

  return undefined;
};

export const getPassengerScopeRestrictionTooltipKeys = (
  restrictions: TPassengerScopeRestrictions,
): string[] => {
  const keys: string[] = [];

  if (restrictions.isForeignUnit) {
    keys.push("ride.passengerTooltip.foreignUnit");
  }

  if (restrictions.isForeignSite) {
    keys.push("ride.passengerTooltip.foreignSite");
  }

  return keys;
};

/**
 * Edit / open passenger row UI gate.
 * Cases: both unit and site checked when present; missing ⇒ unrestricted.
 * ride.siteId ignored.
 */
export const isPassengerAuthorizedByUnitAndSiteScope = (
  passenger: TPassengerWithScope,
  profileAuthorizationInCheck: TProfileAuthorizationCheck,
): boolean =>
  profileAuthorizationInCheck({
    unitIds:
      passenger.unitId && Number(passenger.unitId)
        ? [Number(passenger.unitId)]
        : undefined,
    siteIds:
      passenger.siteId && Number(passenger.siteId)
        ? [Number(passenger.siteId)]
        : undefined,
  });

export type TCanDragPassengerByProfileScopeParams = {
  passenger: TPassengerWithScope;
  profileAuthorizationInCheck: TProfileAuthorizationCheck;
} & TSiteScopeRemoveOptions;

/** Drag / move passenger to another ride — same unit + site gate as remove. */
export const canDragPassengerByProfileScope = ({
  passenger,
  profileAuthorizationInCheck,
  hasCreateOtherSite = false,
}: TCanDragPassengerByProfileScopeParams): boolean =>
  canRemovePassengerByProfileScope(passenger, profileAuthorizationInCheck, {
    hasCreateOtherSite,
  });

/** EVERY unit: all station passengers pass unit scope (or have no unit). */
const everyPassengerPassesUnitScope = (
  stationPassengers: TPassengerWithScope[],
  profileAuthorizationInCheck: TProfileAuthorizationCheck,
): boolean =>
  stationPassengers.every(passenger =>
    isPassengerAuthorizedByUnitScope(passenger, profileAuthorizationInCheck),
  );

/**
 * EVERY sites (DELETE).
 * Cases:
 * 1. empty station → allow
 * 2. every passenger site-authorized or no-site → allow
 * 3. any foreign-site passenger → block
 */
export const everyPassengerPassesSiteScope = ({
  stationPassengers,
  isPassengerAuthorized,
}: {
  stationPassengers: { siteId?: number | null }[];
  isPassengerAuthorized: (passenger: { siteId?: number | null }) => boolean;
}): boolean => {
  if (!stationPassengers.length) {
    return true;
  }

  return stationPassengers.every(isPassengerAuthorized);
};

/**
 * ANY sites — generic helper (listing unlock / mixed-ride visibility).
 * Cases:
 * 1. empty station → allow
 * 2. at least one own-site or no-site passenger → allow
 * 3. foreign-only passengers → block
 */
export const anyPassengerPassesSiteScope = ({
  stationPassengers,
  isPassengerAuthorized,
}: {
  stationPassengers: { siteId?: number | null }[];
  isPassengerAuthorized: (passenger: { siteId?: number | null }) => boolean;
}): boolean => {
  if (!stationPassengers.length) {
    return true;
  }

  return stationPassengers.some(isPassengerAuthorized);
};

/**
 * Station DELETE UI gate.
 * Default: EVERY unit AND EVERY site. create_other_site: EVERY unit only.
 * BE: validateRideRemovePassengersSameSites.
 */
export const canRemoveStationByProfileScope = ({
  stationPassengers,
  profileAuthorizationInCheck,
  hasCreateOtherSite = false,
}: {
  stationPassengers: TPassengerWithScope[];
  profileAuthorizationInCheck: TProfileAuthorizationCheck;
} & TSiteScopeRemoveOptions): boolean => {
  if (
    !everyPassengerPassesUnitScope(
      stationPassengers,
      profileAuthorizationInCheck,
    )
  ) {
    return false;
  }

  if (hasCreateOtherSite) {
    return true;
  }

  return everyPassengerPassesSiteScope({
    stationPassengers,
    isPassengerAuthorized: passenger =>
      isPassengerAuthorizedBySiteScope(passenger, profileAuthorizationInCheck),
  });
};

/** Station DRAG / reorder — no unit / site ACL (legacy). */
export const canReorderStationByProfileScope = (): boolean => true;

/** True when any passenger has missing / falsy siteId (no-site). */
export const hasPassengerWithoutSite = (
  passengersInfo: { siteId?: number | null }[] | undefined,
): boolean =>
  (passengersInfo ?? []).some(
    passenger => !passenger.siteId || !Number(passenger.siteId),
  );

/**
 * Display only (mixed-site badge) — not ACL.
 * Cases: missing site treated as null; mixed when >1 distinct keys.
 */
export const isMixedStationBySite = (
  passengers: { siteId?: number | null }[],
): boolean => {
  if (!passengers.length) {
    return false;
  }

  const normalizedSiteIds = passengers.map(passenger =>
    passenger.siteId && Number(passenger.siteId)
      ? Number(passenger.siteId)
      : null,
  );

  return new Set(normalizedSiteIds).size > 1;
};
